/**
 * Schemas service — fetch one schema on demand, and write them (create / update
 * / usage / remove). The catalog (`./catalog.ts`) holds and loads all of them.
 *
 * The write rules live here, not in an interface: name trimming and length,
 * field cleaning and validation, and the in-use guard on delete. Every write
 * returns the written row and hands it to the catalog, which updates its copy
 * and marks it unchecked, so the next refresh reloads.
 *
 * Who may write which schema is Hasura's rule, not this service's: admins
 * write any schema and pass AS_ADMIN; staff (no role option) may write only a
 * list's own schemas — a schema that no type and no non-list record uses. So
 * `create` and `update` take the caller's MutateOptions and pass them through.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import { cleanSchemaFields, hasFieldType, type SchemaFieldDraft, validateSchemaField } from "../fields/fields.js"
import { parseSchemaRow } from "../fields/parse.js"
import {
  deleteSchema,
  fetchSchemaById,
  fetchSchemaUsage,
  insertSchema,
  updateSchema,
} from "../gql/schemas.js"
import type { Mutate, MutateOptions, Query } from "../index.js"
import {
  getSchema,
  type Schema,
  type SchemaField,
  type SchemaLayout,
} from "../registries/schemas.js"
import type { CatalogService } from "./catalog.js"

const NAME_MAX = 255

export interface CreateSchemaInput {
  name: string
  description?: string | null
  fields: SchemaFieldDraft[]
  layout?: Record<string, unknown> | null
  record_type?: string | null
  icon?: string | null
  color?: string | null
}

/** Only the keys present are written. */
export interface UpdateSchemaPatch {
  name?: string
  description?: string | null
  fields?: SchemaFieldDraft[]
  layout?: Record<string, unknown> | null
  record_type?: string | null
  icon?: string | null
  color?: string | null
}

export interface SchemaUsage {
  recordCount: number
  /** Lists whose listing schema is this one. */
  lists: Array<{ id: number; name: string }>
  listCount: number
  /** Record types whose default schema is this one. */
  types: string[]
  inUse: boolean
}

/** Thrown by `remove` when the schema is still referenced and `force` is unset. */
export class SchemaInUseError extends Error {
  readonly usage: SchemaUsage
  constructor(usage: SchemaUsage) {
    const types = usage.types.length ? ` and is the default schema of type(s) ${usage.types.join(", ")}` : ""
    super(
      `Schema is in use by ${usage.recordCount} record(s) and ${usage.listCount} list(s)${types}. ` +
        `Use force=true to delete anyway.`
    )
    this.name = "SchemaInUseError"
    this.usage = usage
  }
}

function checkNameLength(trimmed: string): void {
  // Pre-validate against the schemas.name varchar(255) column so an over-long
  // name fails with an actionable message instead of Postgres's opaque one.
  if (trimmed.length > NAME_MAX) {
    throw new Error(
      `Schema name is too long (${trimmed.length} chars); the maximum is ${NAME_MAX}. ` +
        `Use a short name and put detail in \`description\`.`
    )
  }
}

/** Clean the drafts for storage, then validate every field and report all
 *  problems in one error. A draft that cleaning would drop (no key or label) is
 *  validated as authored, so it is reported rather than silently lost. */
function cleanAndValidateFields(drafts: readonly SchemaFieldDraft[]) {
  const problems: string[] = []
  drafts.forEach((draft, i) => {
    // Cleaning refuses an unknown type; validate that draft as authored, so the
    // error joins the others instead of ending the report.
    const one = hasFieldType(draft.type || "string") ? cleanSchemaFields([draft])[0] : undefined
    const { errors } = validateSchemaField(one ?? draft)
    if (errors.length) {
      const label = draft.key ? `"${draft.key}"` : `#${i + 1}`
      problems.push(`field ${label}: ${errors.join("; ")}`)
    }
  })
  if (problems.length) throw new Error(`Invalid schema fields — ${problems.join(" | ")}`)
  return cleanSchemaFields(drafts)
}

type SchemaFieldsRow = NonNullable<ResultOf<typeof fetchSchemaById>["schema"]>

/** A schema as the service returns it: SchemaFields, its jsonb `fields` and
 *  `layout` read as the field list and layout they hold. */
export type SchemaRow = Omit<SchemaFieldsRow, "fields" | "layout"> & {
  fields: SchemaField[]
  layout: SchemaLayout | null
}

/** A selected schema with its `fields` and `layout` jsonb parsed (checked at
 *  run time; a malformed entry throws — see fields/parse.ts). */
export const asSchemaRow = (row: SchemaFieldsRow): SchemaRow => parseSchemaRow(row)

export function createSchemasService({
  query,
  mutate,
  catalog,
}: {
  query: Query
  mutate: Mutate
  catalog: Pick<CatalogService, "written">
}) {
  const get = async (id: number): Promise<Schema | null> => {
    const cached = getSchema(id)
    if (cached) return cached
    const row = (await query(fetchSchemaById, { id })).schema
    return row ? asSchemaRow(row) : null
  }

  const written = async (row: SchemaRow): Promise<SchemaRow> => {
    await catalog.written({ schema: row })
    return row
  }

  /** Pass AS_ADMIN for a data-model write (a type's or a record schema).
   *  Without it the write runs as the caller's default role: staff may create
   *  a schema, and write one that no type and no non-list record uses. */
  const create = async (input: CreateSchemaInput, opts?: MutateOptions): Promise<SchemaRow> => {
    const name = (input.name ?? "").trim()
    if (!name) throw new Error("Schema name is required")
    checkNameLength(name)
    const fields = cleanAndValidateFields(input.fields ?? [])

    const data = await mutate(insertSchema, {
      object: {
        name,
        description: input.description?.trim() || null,
        // Cast kept: codegen types every jsonb input as an object
        // (Record<string, unknown>), and schemas.fields is the one array-shaped
        // jsonb column. The value is cleanAndValidateFields' output.
        fields: fields as unknown as Record<string, unknown>,
        ...(input.layout !== undefined ? { layout: input.layout } : {}),
        record_type: input.record_type || null,
        icon: input.icon ?? null,
        color: input.color ?? null,
      },
    }, opts)
    const row = data.insert_schemas_one
    if (!row) throw new Error("Failed to create schema")
    return written(asSchemaRow(row))
  }

  /** Admins pass AS_ADMIN, as for `create`. A staff update of a schema the
   *  rule hides matches no row and throws "not found". */
  const update = async (id: number, patch: UpdateSchemaPatch, opts?: MutateOptions): Promise<SchemaRow> => {
    const _set: Record<string, unknown> = {}

    if (patch.name !== undefined) {
      const name = patch.name.trim()
      if (!name) throw new Error("Schema name cannot be empty")
      checkNameLength(name)
      _set.name = name
    }
    if (patch.description !== undefined) _set.description = patch.description?.trim() || null
    if (patch.fields !== undefined) _set.fields = cleanAndValidateFields(patch.fields)
    if (patch.layout !== undefined) _set.layout = patch.layout
    if (patch.record_type !== undefined) _set.record_type = patch.record_type
    if (patch.icon !== undefined) _set.icon = patch.icon
    if (patch.color !== undefined) _set.color = patch.color

    if (Object.keys(_set).length === 0) throw new Error("No changes provided")

    const data = await mutate(updateSchema, { id, _set }, opts)
    const row = data.update_schemas_by_pk
    if (!row) throw new Error(`Schema with id ${id} not found`)
    return written(asSchemaRow(row))
  }

  const usage = async (id: number): Promise<SchemaUsage> => {
    const data = await query(fetchSchemaUsage, { id })
    const recordCount = data.records_aggregate.aggregate?.count ?? 0
    const lists = data.lists ?? []
    const types = (data.types ?? []).map((t) => t.name)
    return {
      recordCount,
      lists,
      listCount: lists.length,
      types,
      inUse: recordCount > 0 || lists.length > 0 || types.length > 0,
    }
  }

  /** Delete a schema. A schema still referenced by records, lists or types is
   *  refused with `SchemaInUseError` unless `force` is set. Returns the deleted row. */
  const remove = async (id: number, opts: { force?: boolean } = {}): Promise<SchemaRow> => {
    if (!opts.force) {
      const u = await usage(id)
      if (u.inUse) throw new SchemaInUseError(u)
    }
    const data = await mutate(deleteSchema, { id })
    const row = data.delete_schemas_by_pk
    if (!row) throw new Error(`Schema with id ${id} not found`)
    await catalog.written({ removedSchemaId: id })
    return asSchemaRow(row)
  }

  return { get, create, update, usage, remove }
}
